// Small, safe markdown → HTML renderer for assistant answers.
// Escapes all HTML first; supports headings, rules, GFM tables, bullet/numbered lists,
// bold, italic and inline code. No links or raw HTML (by design).

export function escapeHtml(text) {
  return String(text ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inline(s) {
  return s
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[^*])\*([^*\s][^*]*)\*(?!\*)/g, "$1<em>$2</em>");
}

const isRow = (l) => /^\s*\|.*\|\s*$/.test(l);
const isSep = (l) =>
  /^\s*\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?\s*$/.test(l);
const cells = (l) =>
  l
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((c) => c.trim());
const align = (c) =>
  /^:-+:$/.test(c) ? "center" : /-+:$/.test(c) ? "right" : "";

export function renderMarkdown(text) {
  if (!text) return "";
  const lines = escapeHtml(text).split("\n");
  const out = [];
  let list = null;
  const closeList = () => {
    if (list) out.push(`</${list}>`);
    list = null;
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isRow(line) && i + 1 < lines.length && isSep(lines[i + 1])) {
      closeList();
      const head = cells(line);
      const al = cells(lines[i + 1]).map(align);
      const td = (tag, c, j) =>
        `<${tag}${al[j] ? ` style="text-align:${al[j]}"` : ""}>${inline(c)}</${tag}>`;
      const rows = [];
      i += 2;
      while (i < lines.length && isRow(lines[i])) rows.push(cells(lines[i++]));
      i--;
      out.push(
        `<div class="md-table-wrap"><table class="md-table"><thead><tr>${head.map((c, j) => td("th", c, j)).join("")}</tr></thead><tbody>` +
          rows
            .map(
              (r) =>
                `<tr>${head.map((_, j) => td("td", r[j] ?? "", j)).join("")}</tr>`,
            )
            .join("") +
          "</tbody></table></div>",
      );
      continue;
    }
    const h = /^\s*(#{1,6})\s+(.*)$/.exec(line);
    if (h) {
      closeList();
      const lvl = Math.min(h[1].length + 2, 6);
      out.push(`<h${lvl} class="md-h">${inline(h[2])}</h${lvl}>`);
      continue;
    }
    if (/^\s*([-*_])(\s*\1){2,}\s*$/.test(line)) {
      closeList();
      out.push("<hr/>");
      continue;
    }
    const ul = /^\s*[•\-*]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (ul || ol) {
      const kind = ul ? "ul" : "ol";
      if (list !== kind) {
        closeList();
        out.push(`<${kind}>`);
        list = kind;
      }
      out.push(`<li>${inline((ul || ol)[1])}</li>`);
      continue;
    }
    closeList();
    if (line.trim() === "") out.push("<br/>");
    else out.push(`<div>${inline(line)}</div>`);
  }
  closeList();
  return out.join("");
}
